import { SearchMapRowPricesContext } from './SearchMapRowPrices'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { SearchMapRedesignContext } from './SearchMapRedesignContext'
import MapStyleToggleControl from 'tripPlanner/components/ChatMap/MapStyleToggleControl'
import type { MapStyleMode } from 'tripPlanner/components/ChatMap/map3DConfig'
import { PROPERTY_MAP_ROADMAP_STYLE, PROPERTY_MAP_SATELLITE_STYLE } from 'components/Common/PropertyMap/constants'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import mapboxgl, { type ErrorEvent as MapboxErrorEvent } from 'mapbox-gl'
import MapboxBaseMap, { Marker, NavigationControl, type MapEvent, type MapRef, type ViewStateChangeEvent } from 'react-map-gl/mapbox'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import clsx from 'clsx'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useSearchMapResults from './useSearchMapResults'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import usePrevious from 'hooks/usePrevious'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import {
  getBoundsDisambiguationCoordinates,
  getDisplayMarkerIdsMatchingOfferIds,
  getMarkersByOffers,
  getOfferId,
  getVisibleMapOfferCount,
  isLoadedOffer,
  isOfferListMeta,
  getMapPinReplaceDecision,
  softMergeSearchMapPins,
  isBoundsDrivenOfferListChange,
  useVisitedMarkersState,
} from './utils'
import { getPadding } from './searchBoundsConfig'

import { MapboxGlobalStyles } from 'components/Common/Mapbox/MapboxGlobalStyles'
import { MapboxAttributionControl } from 'components/Common/Mapbox/MapboxAttributionControl'
import config from 'constants/config'
import { getMapSearchMarkerCap, getMapSearchPageSize } from 'constants/searchHelpers'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { ANYWHERE_PLACE_ID, BALI_LAT_LNG } from 'constants/search'
import HotelMapMarker from 'components/Pages/HotelSearchPage/HotelMapMarker/HotelMapMarker'
import SearchMapFlashPriceMarker from './SearchMapFlashPriceMarker'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useRebrandVersion from 'hooks/useRebrandVersion'
import TextButton from 'LuxKit/Button/TextButton'
import { sortBy, take } from 'lib/array/arrayUtils'
import Snackbar from 'LuxKit/Snackbar/Snackbar'
import useOffers from 'hooks/Offers/useOffers'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import getOfferListKey from 'lib/offer/offerListKey'
import useStableObjectReference from 'hooks/useStableObjectReference'
import {
  getMapViewportBounds,
  mapBoundsToMapboxBounds,
  parseMapBounds,
} from 'lib/maps/bounds'
import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import {
  centerAndZoomOnMarker,
  centrePinForMobile,
  fitBoundsOnMap,
  fitMarkersOnMap,
  getAdjustedZoom,
  isMarkerWithinBounds,
  isTargetPropertySearchOffer,
  mapHasAnyMarkersInView,
  parseOfferListBounds,
  shouldDisplayOffer,
} from 'lib/maps/mapboxSearchMap'
import type { MapBounds } from 'lib/maps/types'
import { useAppSelector } from '../../../hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { OfferBestPriceData } from 'selectors/offerSelectors'
import resolveLuxPlusMemberPrice from 'lib/search/resolveLuxPlusMemberPrice'
import { reportLog } from 'services/userMonitoring'
import { usePlaceById } from 'hooks/usePlace'
import PointOfInterestMarker from './PointOfInterestMarker'
import zIndex from 'styles/tools/z-index'
import {
  shouldMarkInteractionFromMoveStart,
  shouldMarkInteractionFromNativeEvent,
  shouldReapplySearchCameraAfterResultsChange,
  shouldRestoreReusedMapCamera,
  shouldTriggerBoundsSearch,
} from './mapInteractionUtils'
import { useWishlistIndex } from 'wishlists/hooks'
import { getWishlistedOfferIds } from 'wishlists/fingerprint'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'

const MapContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  isolation: isolate;
  z-index: 0;

  &.redesign {
    ${mediaQueryUp.largeDesktop} { border-start-start-radius: ${rem(20)}; }
    .mapboxgl-ctrl-group { border-radius: ${rem(8)}; }
    .mapboxgl-ctrl-group button { width: ${rem(40)}; height: ${rem(40)}; }
    .mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-group { margin-block: 0 ${rem(4)}; margin-inline: 0 ${rem(12)}; }
    .mapboxgl-ctrl-group button:has(img) { border: 2px solid white; border-radius: ${rem(8)}; }
    ${mediaQueryDown.mobile} {
      .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl-attrib {
        margin-block-end: max(${rem(8)}, env(safe-area-inset-bottom));
        margin-inline-start: ${rem(8)};
      }
    }
  }

  &.showPlaceholder {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

const FloatingBanner = styled.div`
  position: absolute;
  z-index: ${zIndex.floatingPanel};
  inset-inline-start: 50%;
  transform: translateX(-50%);
  top: 32px;

  [dir='rtl'] & {
    transform: translateX(50%);
  }
  &.redesign {
    inset-inline-start: auto;
    inset-inline-end: ${rem(12)};
    top: ${rem(12)};
    transform: none;
    [dir='rtl'] & { transform: none; }
    && > * {
      border-radius: ${rem(100)};
      height: ${rem(32)};
      min-height: ${rem(32)};
      font-size: ${rem(14)};
      font-weight: 400;
      line-height: ${rem(20)};
      padding: ${rem(4)} ${rem(12)};
      background: ${props => props.theme.palette.neutral.surface.normalBackground};
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
    &.empty-results {
      top: ${rem(56)};
      inset-inline: ${rem(12)};
      width: fit-content;
      max-width: calc(100% - ${rem(24)});
      margin-inline: auto;
      && > * { height: auto; border-radius: ${rem(8)}; }
    }
  }
`

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

const MapUnavailableMessage = styled(VerticalSpacer)`
  align-items: center;
  max-width: 360px;
  margin: 0 auto;
  padding: 24px;
  text-align: center;
`

/** z-index for the landmark/POI overlay – intentionally equal to pinLimit so it renders above normal hotel markers */
const LANDMARK_PIN_Z_INDEX = config.search.mapView.pinLimit

export interface HoveredOffer {
  id: string
  type: string
}

interface Props {
  filters: App.OfferListFilters
  onBoundsChanged: (newBounds: MapBounds) => void
  onSelectOffers: (offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>) => void
  onDeselectOffers: () => void
  onError: () => void
  onZeroResults: () => void
  selectedOfferIds: Array<string>
  handleProductClick?: (offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary, position: number) => void
  onHover?: (offerId: string | undefined, type: string | undefined) => void
  externalHoveredOffer?: HoveredOffer
  rootRef?: React.RefObject<HTMLDivElement | null>
  offerListOverride?: App.OfferList
  /**
   * @deprecated This attribute is not to be used
   */
  fallbackOfferList?: App.OfferList
  pageSize?: number
  offerList?: App.OfferList
  fullScreenMap: boolean
  zeroResultsBanner: boolean
  mobileCardHeight?: number
  showPriceOnPins?: boolean
  hideZoomControl?: boolean
  disablePinLimit?: boolean
  onMapReady?: (map: mapboxgl.Map) => void
  showFloatingTotalCount?: boolean
  showOfferCardOnMapMarkerClick?: boolean
  showPerNightPricing?: boolean
  /**
   * When true, prevents the map from updating its bounds/position.
   * Used during typeahead extraction to keep the map on the current location.
   */
  isSearchBlocked?: boolean
  cameraZoomAdjustment?: number
  /**
   * Passed from map pages that own experiment activation and paging limits.
   */
  geoDiversityEnabled?: boolean
  markerCap?: number
}

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const typeOrder: Record<string, number> = {
  hotel: 1,
  tactical_ao_hotel: 2,
  bedbank_hotel: 3,
}

const MARKER_DISPLAY_TIMEOUT = 2000
const WEBGL_ERROR_PATTERN = /webgl|hardware acceleration|graphics|gpu|context/i

function releaseWebGLContext(context: RenderingContext | null) {
  if (!context || !('getExtension' in context)) {
    return
  }

  context.getExtension('WEBGL_lose_context')?.loseContext()
}

function getBrowserMapCapabilityContext() {
  if (typeof window === 'undefined') {
    return { isServer: true }
  }

  const canvas = document.createElement('canvas')
  const navigatorWithDeviceMemory = navigator as Navigator & { deviceMemory?: number }
  const webglContext = canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl')
  const webgl2Context = canvas.getContext('webgl2')

  const context = {
    isServer: false,
    mapboxGlSupported: mapboxgl.supported(),
    webglAvailable: !!webglContext,
    webgl2Available: !!webgl2Context,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: navigatorWithDeviceMemory.deviceMemory,
    pathname: window.location.pathname,
  }

  releaseWebGLContext(webglContext)
  releaseWebGLContext(webgl2Context)

  return context
}

function isMapboxWebGLError(message: string) {
  return WEBGL_ERROR_PATTERN.test(message)
}

function isTrackableProduct(offer: App.Offer | App.BedbankOffer | App.OfferListMetaData): offer is App.Offer | App.BedbankOffer {
  return 'id' in offer
}

type SearchMapPinOffer =
  App.HotelOffer |
  App.BedbankOffer |
  App.HotelOfferSummary |
  App.BedbankOfferSummary |
  App.OfferListMetaData

type RecentSearchPinResults = Array<Array<SearchMapPinOffer>>

function withUpdatedRecentPinsIfChanged(
  prev: RecentSearchPinResults,
  newValue: Array<SearchMapPinOffer>,
): RecentSearchPinResults {
  const prevValue = prev[0]
  if (prevValue && newValue.length === prevValue.length && newValue.every((offer, i) => offer === prevValue[i])) {
    return prev
  }
  return [newValue]
}

function withSoftMergedRecentPins(
  prev: RecentSearchPinResults,
  nextOffers: Array<SearchMapPinOffer>,
  limit?: number,
): RecentSearchPinResults {
  const previousPins = prev[0] ?? []
  return withUpdatedRecentPinsIfChanged(
    prev,
    softMergeSearchMapPins(previousPins, nextOffers, getOfferId, limit),
  )
}

interface MarkerPriceDataParams {
  marker: App.SearchMapMarker
  effectiveHoveredOffer?: HoveredOffer
  offerIdToMarkerIdMap: Map<string, string>
  currentOffersBestPrices: Record<string, OfferBestPriceData>
  offerMetaDataState: App.State['offer']['searchResultMetadata']['offerMetaData']
  offerMetaDataKey: string
  prevOfferMetaDataKey?: string
  hasDates: boolean
  canViewLuxPlusBenefits: boolean
  showPriceOnPins: boolean
  isSoldOut: boolean
  formatCurrency: ReturnType<typeof useCurrencyFormatter>
}

interface MarkerPriceDataResult {
  isMarkerHovered: boolean
  priceLabel?: string
  isLuxPlusPrice: boolean
}

function getMarkerPriceData({
  marker,
  effectiveHoveredOffer,
  offerIdToMarkerIdMap,
  currentOffersBestPrices,
  offerMetaDataState,
  offerMetaDataKey,
  prevOfferMetaDataKey,
  hasDates,
  canViewLuxPlusBenefits,
  showPriceOnPins,
  isSoldOut,
  formatCurrency,
}: MarkerPriceDataParams): MarkerPriceDataResult {
  // 1) Pick which offer drives the marker price. If a grouped marker is hovered,
  // use that hovered offer; otherwise use the marker's primary offer.
  const isMarkerHovered = effectiveHoveredOffer ? offerIdToMarkerIdMap.get(effectiveHoveredOffer.id) === marker.id : false
  const priceOfferId = isMarkerHovered && effectiveHoveredOffer ? effectiveHoveredOffer.id : getOfferId(marker.offers[0])

  // 2) Load both price sources:
  // - live best-price cache (bestPrice)
  // - metadata pricing from current key, with fallback to previous key while data refreshes
  const bestPrice = currentOffersBestPrices[priceOfferId]
  const previousMetadataPricing = prevOfferMetaDataKey
    ? offerMetaDataState[prevOfferMetaDataKey]?.[priceOfferId]?.pricing
    : undefined
  const metadataPricing = offerMetaDataState[offerMetaDataKey]?.[priceOfferId]?.pricing
    ?? previousMetadataPricing
  const resolvedMetadataMemberPrice = resolveLuxPlusMemberPrice({
    metadataLowestMemberPrice: metadataPricing?.lowestMemberPrice,
    referencePrice: metadataPricing?.lowestPrice,
    packagePrice: bestPrice?.bestPricePackage?.price,
    packageMemberPrice: bestPrice?.bestPricePackage?.memberPrice,
    fallbackMemberPrice: bestPrice?.bestMemberPrice,
  })
  const hasExplicitNoMemberPriceFromLiveRate = bestPrice?.bestMemberPrice === 0

  // 3) For each source, determine if a valid LuxPlus member price exists and is lower
  // than the base price. If yes, prefer the member price.
  const hasLuxPlusPrice = !hasExplicitNoMemberPriceFromLiveRate &&
    canViewLuxPlusBenefits && !!bestPrice?.bestMemberPrice && bestPrice.bestMemberPrice > 0 && bestPrice.bestMemberPrice < bestPrice.bestPrice
  const hasMetadataLuxPlusPrice = !hasExplicitNoMemberPriceFromLiveRate &&
    canViewLuxPlusBenefits &&
    !!resolvedMetadataMemberPrice &&
    resolvedMetadataMemberPrice > 0 &&
    !!metadataPricing?.lowestPrice &&
    resolvedMetadataMemberPrice < metadataPricing.lowestPrice

  // 4) Choose a raw display price:
  // - prefer metadata pricing when positive
  // - otherwise fallback to best-price cache
  const metadataDisplayPrice = hasMetadataLuxPlusPrice
    ? resolvedMetadataMemberPrice
    : metadataPricing?.lowestPrice
  const fallbackDisplayPrice = hasLuxPlusPrice ? bestPrice?.bestMemberPrice : bestPrice?.bestPrice
  const hasUsableMetadataDisplayPrice = !!metadataDisplayPrice && metadataDisplayPrice > 0
  const hasUsableFallbackDisplayPrice = !!fallbackDisplayPrice && fallbackDisplayPrice > 0
  const shouldUseMetadataPrice = hasUsableMetadataDisplayPrice && (
    !hasUsableFallbackDisplayPrice || metadataDisplayPrice <= fallbackDisplayPrice
  )
  const displayPrice = shouldUseMetadataPrice
    ? metadataDisplayPrice
    : (hasUsableFallbackDisplayPrice ? fallbackDisplayPrice : undefined)
  const roundedDisplayPrice = displayPrice && displayPrice > 0 ? Math.round(displayPrice) : undefined

  // 5) Build the pin label only when map pin prices are enabled and the offer is
  // truly priceable/available in the current search context.
  const priceLabel = showPriceOnPins && hasDates && !isSoldOut && bestPrice && !bestPrice.soldOut && roundedDisplayPrice && roundedDisplayPrice > 0
    ? formatCurrency(roundedDisplayPrice)
    : undefined

  // 6) Mark the label as LuxPlus only when a label exists, and based on whichever
  // price source was ultimately used for that label.
  const isLuxPlusPrice = !!priceLabel && (hasUsableMetadataDisplayPrice
    ? (shouldUseMetadataPrice ? hasMetadataLuxPlusPrice : hasLuxPlusPrice)
    : hasLuxPlusPrice
  )

  return { isMarkerHovered, priceLabel, isLuxPlusPrice }
}

interface MapMoveEvent extends ViewStateChangeEvent {
  originalEvent?: MouseEvent | TouchEvent | WheelEvent
}

function SearchResultsMap({
  filters,
  fullScreenMap,
  handleProductClick,
  offerList,
  offerListOverride,
  onBoundsChanged,
  onDeselectOffers,
  onError,
  onHover,
  externalHoveredOffer,
  onSelectOffers,
  onZeroResults,
  pageSize,
  rootRef,
  selectedOfferIds,
  zeroResultsBanner,
  mobileCardHeight,
  showPriceOnPins = false,
  hideZoomControl = false,
  disablePinLimit = false,
  onMapReady,
  showFloatingTotalCount = true,
  showOfferCardOnMapMarkerClick = true,
  showPerNightPricing = false,
  isSearchBlocked = false,
  cameraZoomAdjustment = 0,
  geoDiversityEnabled = false,
  markerCap: markerCapProp,
}: Props) {
  const intl = useIntl()
  const redesign = useContext(SearchMapRedesignContext)
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>('standard')
  const floatingStatusKind = useRebrandVersion() === 'rebrand' ? 'primary' : 'secondary'

  const resolvedPageSize = useMemo(
    () => pageSize ?? getMapSearchPageSize(config.search.mapView),
    [pageSize],
  )
  const mapMarkerLimit = useMemo(
    () => markerCapProp ?? getMapSearchMarkerCap(resolvedPageSize, config.search.mapView, geoDiversityEnabled),
    [geoDiversityEnabled, markerCapProp, resolvedPageSize],
  )

  const {
    searchTargetLabel,
  } = useContext(GlobalSearchStateContext)
  const searchKey = getOfferListKey(filters)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)

  const [hoveredOffer, setHoveredOffer] = useState<HoveredOffer | undefined>(undefined)
  const effectiveHoveredOffer = externalHoveredOffer ?? hoveredOffer
  const [recentSearchResults, setRecentSearchResults] = useState<Array<Array<App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.OfferListMetaData>>>([])
  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const [hasInitialCameraApplied, setHasInitialCameraApplied] = useState(false)
  const [hasMapboxWebGLError, setHasMapboxWebGLError] = useState(false)
  const isMapboxSupported = useMemo(() => typeof window === 'undefined' || mapboxgl.supported(), [])

  const mapRef = useRef<MapRef | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasUserInteractedSinceManualSearch = useRef(false)
  const hasLoggedMapboxUnsupported = useRef(false)
  const hasLoggedMapboxRuntimeError = useRef(false)
  const hasHandledMapboxWebGLError = useRef(false)

  const setSearchLabel = useCallback((searchLabel: string) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_TARGET_LABEL, searchTargetLabel: searchLabel })
  }, [globalSearchDispatch])

  useEffect(() => {
    if (isMapboxSupported || hasLoggedMapboxUnsupported.current) {
      return
    }

    hasLoggedMapboxUnsupported.current = true
    reportLog('Search results map WebGL unsupported', getBrowserMapCapabilityContext(), 'warn')
  }, [isMapboxSupported])

  const handleMapboxError = useCallback((event: MapboxErrorEvent) => {
    const errorMessage = event.error?.message ?? 'Unknown Mapbox error'

    if (!hasLoggedMapboxRuntimeError.current) {
      hasLoggedMapboxRuntimeError.current = true
      reportLog('Search results map Mapbox runtime error', {
        ...getBrowserMapCapabilityContext(),
        errorMessage,
        errorType: event.type,
      }, isMapboxWebGLError(errorMessage) ? 'error' : 'warn')
    }

    if (isMapboxWebGLError(errorMessage)) {
      if (hasHandledMapboxWebGLError.current) {
        return
      }

      hasHandledMapboxWebGLError.current = true
      setHasMapboxWebGLError(true)
      onError()
    }
  }, [onError])

  const currentSearchResult = useSearchMapResults(
    selectedOfferIds,
    offerListOverride,
    resolvedPageSize,
    offerList,
    filters,
    geoDiversityEnabled,
    geoDiversityEnabled && !disablePinLimit ? mapMarkerLimit : undefined,
  )

  const { allOffersLoaded, fetchingPage, offersInteractable } = currentSearchResult
  const currentOfferListKey = currentSearchResult.key ?? searchKey

  /*
    Normally when the map goes idle, it means the user has manually panned/zoomed/rotated,
    and we should do a new bounds search.
    But it also happens when the map loads, or when the map bounds are moved programmatically,
    or when the map size changes due to resizing the window or dismissing a notification.
    We can distinguish between these scenarios with a combination of:
    - Tracking whether the map has been panned/zoomed/rotated, via `hasInteracted` ref
    - Explicitly ignoring the next `onIdle` event, via `ignoreNextIdle` ref
    Neither method on its own is quite enough to handle all edge cases.
    Ideally the map would have a built-in callback for this, but it doesn't.
  */
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  const lastProgrammaticMoveTime = useRef<number>(0)

  const PROGRAMMATIC_MOVE_GRACE_PERIOD = 300

  const isWithinProgrammaticMoveWindow = useCallback(() => {
    const timeSinceLastMove = Date.now() - lastProgrammaticMoveTime.current
    return timeSinceLastMove < PROGRAMMATIC_MOVE_GRACE_PERIOD
  }, [])

  const markMapAsInteracted = useCallback(() => {
    if (!isWithinProgrammaticMoveWindow()) {
      hasUserInteractedSinceManualSearch.current = true
      ignoreNextIdle.current = false
      hasInteracted.current = true
    }
  }, [isWithinProgrammaticMoveWindow])

  const onInteract = useCallback((event: MapMoveEvent) => {
    if (shouldMarkInteractionFromMoveStart(!!event.originalEvent, isWithinProgrammaticMoveWindow())) {
      markMapAsInteracted()
    }
  }, [isWithinProgrammaticMoveWindow, markMapAsInteracted])

  // Check if this is a new destination or property search, in which case we need to update the map position
  const prevAllOffersLoaded = usePrevious(allOffersLoaded, false)
  const prevFilters = usePrevious(filters, {})
  const prevOfferListKey = usePrevious(currentOfferListKey)
  const isNewlyLoaded = allOffersLoaded && (currentOfferListKey !== prevOfferListKey || !prevAllOffersLoaded) && !fetchingPage
  const isNewDestination = !!filters.destinationId && filters.destinationId !== prevFilters.destinationId
  const isNewProperty = !!filters.propertyId && filters.propertyId !== prevFilters.propertyId
  const isNewLandmark = !!filters.landmarkId && filters.landmarkId !== prevFilters.landmarkId
  const isNewNaturalLanguageQuery = !!filters.query && filters.query !== prevFilters.query
  const hasSearchDates = !!(filters.checkIn && filters.checkOut)
  const isNewDates = hasSearchDates && (filters.checkIn !== prevFilters.checkIn || filters.checkOut !== prevFilters.checkOut)
  const isNewRooms = JSON.stringify(filters.rooms) !== JSON.stringify(prevFilters.rooms)
  const isNewBounds = !!filters.bounds && filters.bounds !== prevFilters.bounds
  const isNewHolidayTypes = JSON.stringify(filters.holidayTypes) !== JSON.stringify(prevFilters.holidayTypes)
  const isNewAmenities = JSON.stringify(filters.amenities) !== JSON.stringify(prevFilters.amenities)
  // we distinguish between when bounds are changed vs when filters are changed, because for the former we can still show previous results
  const isNewManualSearch = isNewDestination || isNewProperty || isNewLandmark || isNewNaturalLanguageQuery || isNewDates || isNewRooms || isNewHolidayTypes || isNewAmenities
  const fetchingList = (offerList?.offerIds?.length || 0) < (offerListOverride?.offerCount || 0)

  const currentFilters = useOfferListFilters(filters, { disabled: true })

  const isNewSearch = isNewManualSearch || isNewBounds
  const lastSearch = useRef({
    destination: false,
    property: false,
    landmark: false,
    query: false,
    bounds: false,
    dates: false,
    rooms: false,
  })
  if (isNewSearch) {
    lastSearch.current = {
      destination: isNewDestination,
      property: isNewProperty,
      landmark: isNewLandmark,
      query: isNewNaturalLanguageQuery,
      bounds: isNewBounds,
      dates: isNewDates,
      rooms: isNewRooms,
    }
  }

  if (isNewManualSearch) {
    hasUserInteractedSinceManualSearch.current = false
  }

  const [markerDisplayTimedOut, setMarkerDisplayTimedOut] = useState(false)

  // Treatment soft-merge clamps to marker cap (60), not list page size (20).
  const pinSoftMergeLimit = disablePinLimit
    ? undefined
    : (geoDiversityEnabled ? mapMarkerLimit : resolvedPageSize)

  const clampOffersForDisplay = useCallback((
    offers: Array<App.HotelOffer | App.BedbankOffer | App.HotelOfferSummary | App.BedbankOfferSummary | App.OfferListMetaData>,
  ) => {
    if (pinSoftMergeLimit == null) {
      return offers
    }
    return take(offers, pinSoftMergeLimit)
  }, [pinSoftMergeLimit])

  useEffect(() => {
    setMarkerDisplayTimedOut(false)
    const timeoutId = setTimeout(() => {
      setMarkerDisplayTimedOut(true)
    }, MARKER_DISPLAY_TIMEOUT)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchKey])

  // Treatment: retain pins until the new searchKey has stream data (or settles empty/failed).
  // Soft-merge only for bounds-driven changes; dates/rooms/destination etc. hard-replace.
  const [awaitingPinReplace, setAwaitingPinReplace] = useState(false)
  const [boundsDrivenPinSoftMerge, setBoundsDrivenPinSoftMerge] = useState(false)
  const previousSearchKey = usePrevious(searchKey)
  const searchKeyChanged = previousSearchKey !== undefined && previousSearchKey !== searchKey
  const isAwaitingTreatmentPinReplace = geoDiversityEnabled && (awaitingPinReplace || searchKeyChanged)

  const stableFilters = useStableObjectReference(filters)
  const previousFilters = usePrevious(stableFilters)
  const boundsDrivenChange = isBoundsDrivenOfferListChange(previousFilters, filters)
  const shouldSoftMergeTreatmentPins = geoDiversityEnabled && (
    searchKeyChanged ? boundsDrivenChange : boundsDrivenPinSoftMerge
  )

  useEffect(() => {
    if (!geoDiversityEnabled || !searchKeyChanged) {
      return
    }
    setAwaitingPinReplace(true)
    setBoundsDrivenPinSoftMerge(boundsDrivenChange)
  }, [boundsDrivenChange, geoDiversityEnabled, searchKeyChanged])

  // Control-only soft retention after destination/property/landmark/query changes.
  const [waitingForOffersAfterFilterChange, setWaitingForOffersAfterFilterChange] = useState(false)

  useEffect(() => {
    if (geoDiversityEnabled) {
      return
    }

    const filtersChanged = previousFilters?.destinationId !== filters.destinationId ||
      previousFilters?.propertyId !== filters.propertyId ||
      previousFilters?.landmarkId !== filters.landmarkId ||
      previousFilters?.query !== filters.query

    if (filtersChanged) {
      setWaitingForOffersAfterFilterChange(true)
    }
  }, [
    filters.destinationId,
    filters.landmarkId,
    filters.propertyId,
    filters.query,
    geoDiversityEnabled,
    previousFilters,
  ])

  useEffect(() => {
    if (geoDiversityEnabled) {
      const hasListStreamOffers = (offerList?.offerIds?.length ?? 0) > 0
      const hasMapStreamOffers = (offerList?.mapOfferIds?.length ?? 0) > 0
      // Map-only while list is still fetching is not replace-ready (would shrink retained pins).
      const hasSearchStreamOffers = hasListStreamOffers ||
        (hasMapStreamOffers && !offerList?.fetching)

      const settledEmpty = !hasListStreamOffers && !hasMapStreamOffers &&
        !offerList?.fetching &&
        !currentSearchResult.fetchingPage &&
        (
          currentSearchResult.noOffersAvailable ||
          !!offerList?.streamEnded ||
          !!offerList?.error ||
          !!currentSearchResult.error
        )

      const replaceDecision = getMapPinReplaceDecision({
        awaitingReplace: isAwaitingTreatmentPinReplace,
        hasSearchStreamOffers,
        settledEmpty,
      })

      if (replaceDecision === 'keep') {
        return
      }

      if (replaceDecision === 'clear') {
        setRecentSearchResults([])
        setAwaitingPinReplace(false)
        return
      }

      if (replaceDecision === 'replace') {
        // IDs can land before display offers resolve — keep old pins until we have something to show.
        if (currentSearchResult.offers.length === 0) {
          return
        }
        setAwaitingPinReplace(false)
      } else if (!markerDisplayTimedOut && offerList?.fetching && !offersInteractable && currentSearchResult.fetchingPage) {
        return
      }

      setRecentSearchResults(prev => (
        shouldSoftMergeTreatmentPins
          ? withSoftMergedRecentPins(prev, currentSearchResult.offers, pinSoftMergeLimit)
          : withUpdatedRecentPinsIfChanged(prev, currentSearchResult.offers)
      ))
      return
    }

    // Control: soft-merge previous pins while new results stream in.
    if (markerDisplayTimedOut) {
      if (waitingForOffersAfterFilterChange && currentSearchResult.offers.length > 0) {
        setRecentSearchResults([clampOffersForDisplay(currentSearchResult.offers)])
        setWaitingForOffersAfterFilterChange(false)
        return
      }

      if (!waitingForOffersAfterFilterChange) {
        setRecentSearchResults(prev => withSoftMergedRecentPins(
          prev,
          currentSearchResult.offers,
          pinSoftMergeLimit,
        ))
      }
      return
    }

    if (!offerList?.fetching && (offersInteractable || !currentSearchResult.fetchingPage)) {
      setRecentSearchResults(prev => withUpdatedRecentPinsIfChanged(prev, currentSearchResult.offers))
    }
  }, [
    clampOffersForDisplay,
    currentSearchResult.fetchingPage,
    currentSearchResult.noOffersAvailable,
    currentSearchResult.offers,
    currentSearchResult.error,
    geoDiversityEnabled,
    isAwaitingTreatmentPinReplace,
    markerDisplayTimedOut,
    offerList?.error,
    offerList?.fetching,
    offerList?.mapOfferIds,
    offerList?.offerIds,
    offerList?.streamEnded,
    offersInteractable,
    pinSoftMergeLimit,
    shouldSoftMergeTreatmentPins,
    waitingForOffersAfterFilterChange,
  ])

  useEffect(() => {
    if (currentSearchResult.error) {
      onError()
    }
  }, [currentSearchResult.error, onError])

  const offerMetaDataState = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData)
  const offerMetaDataKey = getOfferListKey(filters)

  const getOfferMetaData = useCallback((offer: App.AnyOffer | App.OfferListMetaData): App.OfferListMetaData | undefined => {
    return offerMetaDataState[offerMetaDataKey]?.[getOfferId(offer)]
      ?? (isOfferListMeta(offer) ? offer : undefined)
  }, [offerMetaDataKey, offerMetaDataState])

  const currentOffers = useMemo(() => {
    // Re-check live search meta here (not only at resolve time) so soft-merged /
    // retained pins update when availability arrives or changes after first paint.
    const displayableOffers = recentSearchResults.flat().filter(offer => shouldDisplayOffer(getOfferMetaData(offer)))

    if (geoDiversityEnabled) {
      // Keep stream merge order for the viewport + markerCap cut.
      return displayableOffers
    }

    return sortBy(
      displayableOffers,
      value => typeOrder[value.type ?? ''],
      'asc',
    )
  }, [geoDiversityEnabled, getOfferMetaData, recentSearchResults])
  const loadedOffers = useMemo(() => currentOffers.filter(isLoadedOffer), [currentOffers])
  const currentOffersBestPrices = useOffersBestPrices(loadedOffers, filters)
  const formatCurrency = useCurrencyFormatter()
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const mapRowPrices = useContext(SearchMapRowPricesContext)

  const isOfferUnavailable = useCallback((offer: App.AnyOffer | App.OfferListMetaData): boolean => (
    getOfferMetaData(offer)?.available === false
  ), [getOfferMetaData])

  const offerMarkers = useMemo(
    () => getMarkersByOffers(currentOffers, true),
    [currentOffers],
  )
  const fullOfferMarkers = useMemo(
    () => getMarkersByOffers(currentOffers, false),
    [currentOffers],
  )
  const boundsDisambiguationCoordinates = useMemo(
    () => getBoundsDisambiguationCoordinates(offerMarkers, offerList?.bounds),
    [offerList?.bounds, offerMarkers],
  )

  const currentBounds = useMemo(
    () => filters.bounds !== undefined
      ? parseMapBounds(
          filters.bounds,
          boundsDisambiguationCoordinates,
        )
      : undefined,
    [boundsDisambiguationCoordinates, filters.bounds],
  )

  const currentMapboxBounds = useMemo(
    () => currentBounds ? mapBoundsToMapboxBounds(currentBounds) : undefined,
    [currentBounds],
  )

  const displayOfferMarkers = useMemo(() => (
    offerMarkers.filter(
      (marker) => !marker.offers.every(
        (offer) => {
          const isSoldOut = isOfferUnavailable(offer)
          return isSoldOut && !isTargetPropertySearchOffer(getOfferMetaData(offer))
        },
      ),
    )
  ), [getOfferMetaData, isOfferUnavailable, offerMarkers])

  const markers = useMemo(() => {
    const markerResult = currentBounds ? displayOfferMarkers.filter((marker) => isMarkerWithinBounds(marker, currentBounds)) : displayOfferMarkers
    const limitedMarkers = (!disablePinLimit && mapMarkerLimit)
      ? take(markerResult, mapMarkerLimit)
      : markerResult

    // Geo-diversity keeps stream order through the cut; re-sort survivors for Flash → LPC → LPP stacking.
    return geoDiversityEnabled
      ? sortBy(limitedMarkers, marker => typeOrder[marker.offers[0]?.type ?? ''], 'asc')
      : limitedMarkers
  }, [currentBounds, disablePinLimit, displayOfferMarkers, geoDiversityEnabled, mapMarkerLimit])

  const prevOfferMetaDataKey = usePrevious(offerMetaDataKey)

  // Maps any offer ID to its display marker ID (co-located offers share one pin)
  const offerIdToMarkerIdMap = useMemo(() => {
    const nextMap = new Map<string, string>()

    for (let i = 0; i < fullOfferMarkers.length; i++) {
      const displayMarkerId = offerMarkers[i]?.id
      if (displayMarkerId) {
        for (const offer of fullOfferMarkers[i].offers) {
          nextMap.set(getOfferId(offer), displayMarkerId)
        }
      }
    }

    return nextMap
  }, [fullOfferMarkers, offerMarkers])

  const activeMarkerId = selectedOfferIds.join(',')
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()

  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const [centrePin, setCentrePin] = useState(true)
  const centeredMobileOfferId = useRef<string | undefined>(undefined)
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const findSelectedMarker = useCallback((nextMarkers: Array<App.SearchMapMarker>, selectedId: string) =>
    nextMarkers.find(marker => marker.offers.some(offer => getOfferId(offer) === selectedId)),
  [])

  const handleMobilePinCentering = useCallback((clickedMarker?: App.SearchMapMarker) => {
    // Early returns for invalid states
    if (isTabletScreenSizeAndUp || !map) return
    if (!clickedMarker && !centrePin) return

    // Use clicked marker or find selected marker
    const markerToCenter = clickedMarker || findSelectedMarker(markers, selectedOfferIds[0])

    if (markerToCenter) {
      if (redesign) {
        lastProgrammaticMoveTime.current = Date.now()
        ignoreNextIdle.current = true
        hasInteracted.current = false
      }
      if (redesign && mobileCardHeight) {
        const height = map.getContainer().clientHeight
        const targetY = Math.max(84, (height - mobileCardHeight - 16 + 56) / 2)
        map.easeTo({ center: [markerToCenter.longitude, markerToCenter.latitude], offset: [0, targetY - height / 2], duration: 0 })
      } else {
        centrePinForMobile(map, markerToCenter)
      }
    }
  }, [isTabletScreenSizeAndUp, map, centrePin, findSelectedMarker, markers, selectedOfferIds, redesign, mobileCardHeight])

  useEffect(() => {
    const selectedOfferId = selectedOfferIds[0]
    if (!selectedOfferId) {
      centeredMobileOfferId.current = undefined
      return
    }
    if (!redesign || !mobileCardHeight || !centrePin || centeredMobileOfferId.current === selectedOfferId) return
    if (!findSelectedMarker(markers, selectedOfferId)) return

    handleMobilePinCentering()
    centeredMobileOfferId.current = selectedOfferId
  }, [redesign, mobileCardHeight, centrePin, selectedOfferIds, markers, findSelectedMarker, handleMobilePinCentering])

  const onMarkerClick = useCallback((marker: App.SearchMapMarker, _pinType: string, e?: Event | React.MouseEvent<Element | MouseEvent>) => {
    setCentrePin(true)
    handleMobilePinCentering(marker)
    onSelectOffers(marker.offers)

    const offer = marker.offers[0]
    if (isTrackableProduct(offer)) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer,
        position: 0,
      })
    }

    if (e) {
      e.stopPropagation()
    }
  }, [handleMobilePinCentering, onSelectOffers, dispatchOfferListEvent])

  // Determine whether to report zero results
  const hadMarkersLoadedOrInViewAtLastLoad = useRef(false)
  useEffect(() => {
    if (isNewlyLoaded) {
      const offersLoadedOrInView = currentSearchResult.totalNumOffers > 0 || mapHasAnyMarkersInView(map, markers)

      if (!offersLoadedOrInView &&
        !currentSearchResult.error &&
        hadMarkersLoadedOrInViewAtLastLoad.current
      ) {
        onZeroResults()
      }
      hadMarkersLoadedOrInViewAtLastLoad.current = offersLoadedOrInView
    }
  }, [currentSearchResult, isNewlyLoaded, map, markers, onZeroResults])

  // If user searched for a property, select it. If user searched for a destination, deselect offers
  const mapAreaText = intl.formatMessage({
    id: 'Qu3mhZKC8SmWKLcUYqSP',
    defaultMessage: 'Map area',
    description: 'Default search label when no destination is selected and results are shown for the visible map area',
  })

  const propertySearchMarker = useMemo(() => filters.propertyId ? markers.find(
    marker => marker.offers.some(offer =>
      (redesign && isTargetPropertySearchOffer(getOfferMetaData(offer))) ||
      ('property' in offer && offer.property?.id === filters.propertyId),
    ),
  ) : undefined, [filters.propertyId, getOfferMetaData, markers, redesign])

  const handledPropertyId = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!filters.propertyId) {
      handledPropertyId.current = undefined
      return
    }
    if (!redesign || !map || isSearchBlocked || !containerRef.current?.offsetWidth ||
      handledPropertyId.current === filters.propertyId) return

    // A property search includes paginated nearby results. Waiting for every
    // result to load would delay opening the searched property's card.
    // Remember the property so later results do not reopen a closed card.
    if (selectedOfferIds.length) {
      handledPropertyId.current = filters.propertyId
      return
    }
    if (propertySearchMarker) {
      handledPropertyId.current = filters.propertyId
      onMarkerClick(propertySearchMarker, 'pin')
    }
  }, [filters.propertyId, isSearchBlocked, map, onMarkerClick, propertySearchMarker, redesign, selectedOfferIds.length])

  useEffect(() => {
    if (isNewlyLoaded) {
      if (!filters.destinationId && !filters.landmarkId && !filters.propertyId && !filters.query && searchTargetLabel !== mapAreaText) {
        setSearchLabel(mapAreaText)
      }
      if (!redesign && lastSearch.current.property && filters.propertyId) {
        const marker = propertySearchMarker
        if (marker) {
          onMarkerClick(marker, 'pin')
        }
      }
    }
  }, [
    filters.destinationId,
    filters.landmarkId,
    filters.propertyId,
    filters.query,
    isNewlyLoaded,
    redesign,
    mapAreaText,
    propertySearchMarker,
    onMarkerClick,
    searchTargetLabel,
    setSearchLabel,
  ])

  const { data: wishlistIndex } = useWishlistIndex()
  const savedMarkerIds = useMemo(() => {
    const savedOfferIds = getWishlistedOfferIds(
      [...fullOfferMarkers, ...offerMarkers].flatMap(marker => marker.offers.map(offer => ({ id: getOfferId(offer), type: offer.type }))),
      wishlistIndex?.savedStateIndex,
    )
    return getDisplayMarkerIdsMatchingOfferIds(fullOfferMarkers, offerMarkers, savedOfferIds)
  }, [fullOfferMarkers, offerMarkers, wishlistIndex?.savedStateIndex])

  const destinationBounds = useMemo(() => {
    if (offerList?.bounds) {
      return parseOfferListBounds(offerList.bounds)
    }

    return undefined
  }, [offerList?.bounds])

  const landmarkPlace = usePlaceById(filters.landmarkId)
  const landmarkCentroid = useMemo(() => {
    if (!filters.landmarkId || !offerList?.centroids) return undefined

    const match = offerList.centroids.find(c => c.placeId === filters.landmarkId)
    if (!match) return undefined

    return match.centroid
  }, [filters.landmarkId, offerList?.centroids])

  // Persist the landmark pin through bounds (pan/zoom) searches.
  // Only clear when the user explicitly navigates to a different search type.
  const persistedLandmark = useRef<{ centroid: { lat: number; lng: number }; label?: string } | undefined>(undefined)
  if (isNewDestination || isNewProperty || isNewNaturalLanguageQuery) {
    persistedLandmark.current = undefined
  } else if (landmarkCentroid) {
    persistedLandmark.current = { centroid: landmarkCentroid, label: landmarkPlace?.name }
  } else if (isNewLandmark) {
    persistedLandmark.current = undefined
  }

  // Fit the correct bounds on load
  const onMapLoad = useCallback((nextMap: mapboxgl.Map) => {
    ignoreNextIdle.current = true
    lastProgrammaticMoveTime.current = Date.now()

    if (filters.destinationId === ANYWHERE_PLACE_ID) {
      nextMap.easeTo({
        center: [BALI_LAT_LNG.lng, BALI_LAT_LNG.lat],
        zoom: getAdjustedZoom(nextMap, 2, cameraZoomAdjustment),
        duration: 0,
      })
    } else if (filters.propertyId) {
      const marker = propertySearchMarker

      if (marker) {
        centerAndZoomOnMarker(nextMap, marker, true, cameraZoomAdjustment)
      } else {
        fitMarkersOnMap(nextMap, markers, getPadding(), cameraZoomAdjustment)
      }
    } else if (destinationBounds) {
      fitBoundsOnMap(nextMap, destinationBounds, { duration: 0 }, cameraZoomAdjustment)
    } else if (filters.bounds && currentMapboxBounds) {
      fitBoundsOnMap(
        nextMap,
        currentMapboxBounds,
        { padding: getPadding(), duration: 0 },
        cameraZoomAdjustment,
      )
    } else if (!selectedOfferIds.length && markers.length) {
      fitMarkersOnMap(nextMap, markers, getPadding(), cameraZoomAdjustment)
    }

    setMap(nextMap)
    setHasInitialCameraApplied(true)
    onMapReady?.(nextMap)
  }, [cameraZoomAdjustment, currentMapboxBounds, destinationBounds, filters.bounds, filters.destinationId, filters.propertyId, markers, onMapReady, propertySearchMarker, selectedOfferIds])

  useEffect(() => {
    // Update the camera if the markers have changed after a destination/property-driven search
    const shouldReapplyCamera = shouldReapplySearchCameraAfterResultsChange(
      !!map,
      isSearchBlocked,
      !fetchingPage || markerDisplayTimedOut,
      (
        lastSearch.current.destination ||
        lastSearch.current.property ||
        lastSearch.current.landmark ||
        lastSearch.current.query
      ),
      hasUserInteractedSinceManualSearch.current,
    )

    if (shouldReapplyCamera && map) {
      ignoreNextIdle.current = true
      lastProgrammaticMoveTime.current = Date.now()

      if (lastSearch.current.property && filters.propertyId) {
        const marker = propertySearchMarker

        if (marker) {
          centerAndZoomOnMarker(map, marker, true, cameraZoomAdjustment)
          handleMobilePinCentering()
          return
        }
      }

      if (filters.destinationId === ANYWHERE_PLACE_ID) {
        map.easeTo({
          center: [BALI_LAT_LNG.lng, BALI_LAT_LNG.lat],
          zoom: getAdjustedZoom(map, 2, cameraZoomAdjustment),
          duration: 0,
        })
      } else if (destinationBounds) {
        fitBoundsOnMap(map, destinationBounds, { duration: 0 }, cameraZoomAdjustment)
        handleMobilePinCentering()
      } else {
        fitMarkersOnMap(map, markers, getPadding(), cameraZoomAdjustment)
        handleMobilePinCentering()
      }
    }
  }, [
    cameraZoomAdjustment,
    destinationBounds,
    fetchingPage,
    filters.destinationId,
    filters.landmarkId,
    filters.propertyId,
    handleMobilePinCentering,
    isSearchBlocked,
    map,
    markerDisplayTimedOut,
    markers,
    propertySearchMarker,
  ])

  useEffect(() => {
    if (!map) {
      return
    }

    const handleNativeMapInteraction = () => {
      // react-map-gl does not always populate originalEvent for real user moves,
      // so the native drag/zoom start events remain the authoritative fallback.
      if (shouldMarkInteractionFromNativeEvent(isWithinProgrammaticMoveWindow())) {
        markMapAsInteracted()
      }
    }

    map.on('zoomstart', handleNativeMapInteraction)
    map.on('dragstart', handleNativeMapInteraction)

    return () => {
      map.off('zoomstart', handleNativeMapInteraction)
      map.off('dragstart', handleNativeMapInteraction)
    }
  }, [isWithinProgrammaticMoveWindow, map, markMapAsInteracted])

  // onBoundsChanged gets a new identity on every fire (onBoundsChanged
  // rewrites the URL, which is how the app notices a new search) — passed as call-time args here,
  // same pattern as usePlaceAutocomplete/useAirportSearch, so they don't sit in this useMemo's deps
  // and invalidate the debounce's timer state on every call.
  const onUserMovedMap = useMemo(() => debounce(
    (
      currentOnBoundsChanged: typeof onBoundsChanged,
    ) => {
      setCentrePin(false)
      const mapBounds = map?.getBounds()

      if (map && mapBounds) {
        const nextBounds = getMapViewportBounds(mapBounds)

        if (nextBounds) {
          currentOnBoundsChanged(nextBounds)
        }
      }
      // Reset after search executes to prevent duplicate searches from subsequent onIdle events
      hasInteracted.current = false
    },
    2000,
    { leading: true, trailing: true, maxWait: 2000 },
  ), [map])

  useEffect(() => {
    return () => {
      onUserMovedMap.cancel()
    }
  }, [onUserMovedMap])

  const onIdle = useCallback(() => {
    const shouldTrigger = shouldTriggerBoundsSearch(ignoreNextIdle.current, hasInteracted.current)

    if (shouldTrigger) {
      onUserMovedMap(onBoundsChanged)
    }

    ignoreNextIdle.current = false
    if (!shouldTrigger) {
      hasInteracted.current = false
    }
  }, [onUserMovedMap, onBoundsChanged])

  useEffect(() => {
    const root = rootRef?.current

    if (!root) {
      return
    }

    const handleOfferHover = (event: Event) => {
      const customEvent = event as CustomEvent<{ id?: string, type?: string, persist?: boolean }>
      const id = customEvent.detail?.id
      const offerType = customEvent.detail?.type ?? ''
      const persist = customEvent.detail?.persist

      if (!id && persist) {
        return
      }

      if (!id) {
        setHoveredOffer(undefined)
        return
      }

      setHoveredOffer({ id, type: offerType })
    }

    root.addEventListener('offerHover', handleOfferHover)

    return () => {
      root.removeEventListener('offerHover', handleOfferHover)
    }
  }, [rootRef])

  // Fetch offer details for only the markers that can currently be clicked
  const singleMarkerOfferIds = useMemo(() => markers.map((marker) => marker.id), [markers])
  useOffers(singleMarkerOfferIds)

  const loadingOffersText = intl.formatMessage({
    id: 'aQJFoyx7-s5KwtUXW50h',
    defaultMessage: 'Loading offers',
    description: 'Button text shown while map offers are loading in the search interface',
  })

  const visibleOfferCount = useMemo(
    () => getVisibleMapOfferCount(markers, fullOfferMarkers),
    [markers, fullOfferMarkers],
  )

  const showingCountText = intl.formatMessage(
    {
      id: 'QiG8YIMVEoMrhFmN8Mag',
      defaultMessage: 'Showing {shown} of {total}',
      description: 'Text showing how many offers are visible on the map out of the total',
    },
    {
      shown: intl.formatNumber(visibleOfferCount),
      total: intl.formatNumber(Number(currentFilters.filters?.total ?? 0)),
    },
  )

  const noOffersFoundText = intl.formatMessage({
    id: 'X_Kpu9vc6te1bDoXsBQ3',
    defaultMessage: 'No offers found in this area',
    description: 'Snackbar message displayed when no offers match in the current map area',
  })

  const clearHover = useCallback(() => {
    setHoveredOffer(undefined)
    onHover?.(undefined, undefined)
  }, [onHover])

  const mapClick = useCallback(() => {
    onDeselectOffers()
    clearHover()
  }, [clearHover, onDeselectOffers])

  const handleMapReady = useCallback((event: MapEvent) => {
    // When returning to the map, its load event can fire before React sets mapRef.
    onMapLoad(event.target)
  }, [onMapLoad])

  useEffect(() => {
    if (map) {
      return
    }

    const existingMap = mapRef.current?.getMap()
    const canRestoreCamera = shouldRestoreReusedMapCamera(
      !!existingMap?.loaded(),
      filters.destinationId === ANYWHERE_PLACE_ID || !!destinationBounds,
      !!(filters.bounds && currentMapboxBounds),
      markers.length > 0,
    )

    // When reuseMaps restores an existing instance, onLoad may not fire again
    // Re-run the initial camera setup only once destination/bounds/marker inputs
    // are available, so we do not briefly keep the previous session's camera.
    if (existingMap && canRestoreCamera) {
      onMapLoad(existingMap)
    }
  }, [currentMapboxBounds, destinationBounds, filters.bounds, filters.destinationId, map, markers.length, onMapLoad])

  const isMobile = useScreenSizeOnly('mobile')
  const minZoom = isMobile ? 1 : 2

  useEffect(() => {
    if (!map) {
      return
    }

    map.setMinZoom(minZoom)
  }, [map, minZoom])

  useEffect(() => {
    if (!map) {
      return
    }

    const container = containerRef.current

    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null

    const scheduleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }

      resizeTimeout = setTimeout(() => {
        if (redesign) {
          lastProgrammaticMoveTime.current = Date.now()
          ignoreNextIdle.current = true
          hasInteracted.current = false
          onUserMovedMap.cancel()
        }
        map.resize()
      }, 0)
    }

    // IMPORTANT: this 0ms timeout is not here to debounce resize events.
    // It defers map.resize() to the next task instead of running it synchronously
    // inside ResizeObserver. Removing this defer makes the map flicker during
    // resize, while non-zero delays make split-view resizing feel low-FPS.
    scheduleResize()

    const observer = new ResizeObserver(() => {
      scheduleResize()
    })

    observer.observe(container)

    return () => {
      observer.disconnect()

      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }
    }
  }, [map, redesign, onUserMovedMap])

  const mapboxAccessToken = getMapboxAccessToken(MAPBOX_ACCESS_TOKEN_USE_CASES.accommodationSearch)

  if (!mapboxAccessToken) {
    return <LoadingIndicator inline />
  }

  if (!isMapboxSupported || hasMapboxWebGLError) {
    return (
      <MapContainer className="showPlaceholder">
        <MapUnavailableMessage gap={8}>
          <Heading variant="heading6">
            {intl.formatMessage({
              id: 'i_FvjNpvxXOzVWxs7ojp',
              defaultMessage: 'Map unavailable',
              description: 'Heading shown in the hotel search map panel when the browser cannot render the interactive map',
            })}
          </Heading>
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage({
              id: 'dwE-K7U6UgyRPtJRs_0Q',
              defaultMessage: 'Your browser could not start the interactive map. You can still browse stays in the list.',
              description: 'Message shown in the hotel search map panel when WebGL or browser graphics support is unavailable',
            })}
          </BodyText>
        </MapUnavailableMessage>
      </MapContainer>
    )
  }

  return (
    <>
      <MapContainer ref={containerRef} className={clsx({ showPlaceholder: !hasInitialCameraApplied, redesign })}>
        <MapboxGlobalStyles />
        <MapboxBaseMap
          ref={mapRef}
          mapboxAccessToken={mapboxAccessToken}
          onLoad={handleMapReady}
          onError={handleMapboxError}
          onClick={mapClick}
          onMoveStart={onInteract}
          onMoveEnd={onIdle}
          style={{
            ...MAP_STYLE,
            visibility: hasInitialCameraApplied ? 'visible' : 'hidden',
          }}
          mapStyle={redesign && mapStyleMode === 'satellite' ? PROPERTY_MAP_SATELLITE_STYLE : PROPERTY_MAP_ROADMAP_STYLE}
          projection="mercator"
          trackResize={!redesign}
          dragRotate={false}
          touchPitch={false}
          renderWorldCopies
          maxZoom={19}
          reuseMaps
          language={intl.locale}
          attributionControl={false}
        >
          <MapboxAttributionControl
            key={redesign && isMobile ? 'mobile' : 'desktop'}
            position={redesign && isMobile ? 'bottom-left' : 'bottom-right'}
          />

          {redesign && !isMobile && <MapStyleToggleControl mapStyleMode={mapStyleMode} onMapStyleModeChange={setMapStyleMode} />}
          {!hideZoomControl && !isMobile && <NavigationControl position="bottom-right" showCompass={false} />}

          {markers.map((marker, index) => {
            const isSoldOut = marker.offers.every(
              (offer) => isOfferUnavailable(offer),
            )
            const { isMarkerHovered, priceLabel, isLuxPlusPrice } = getMarkerPriceData({
              marker,
              effectiveHoveredOffer,
              offerIdToMarkerIdMap,
              currentOffersBestPrices,
              offerMetaDataState,
              offerMetaDataKey,
              prevOfferMetaDataKey,
              hasDates: hasSearchDates,
              canViewLuxPlusBenefits,
              showPriceOnPins: !redesign && showPriceOnPins,
              isSoldOut,
              formatCurrency,
            })

            const markerProps = {
              marker,
              index,
              onMarkerClick,
              isActive: activeMarkerId === marker.id,
              isHovered: isMarkerHovered,
              isVisited: visitedMarkerIds.has(marker.id),
              filters,
              onDeselectOffers,
              handleProductClick,
              onHover,
              soldOut: isSoldOut,
              isSaved: savedMarkerIds.has(marker.id),
              map,
              animateMarker: true,
              showOfferViewWhenActive: showOfferCardOnMapMarkerClick,
              showPerNightPricing,
              isLuxPlusPrice,
            }
            const offer = marker.offers[0]
            return redesign && !isOfferListMeta(offer) && isFlashHotelOffer(offer)
              ? <SearchMapFlashPriceMarker key={marker.id} {...markerProps} offer={offer} priceLabel={mapRowPrices[offer.id]} />
              : <HotelMapMarker key={marker.id} {...markerProps} priceLabel={redesign ? mapRowPrices[getOfferId(offer)] : priceLabel} />
          })}

          {isTabletScreenSizeAndUp && persistedLandmark.current && (
            <Marker
              longitude={persistedLandmark.current.centroid.lng}
              latitude={persistedLandmark.current.centroid.lat}
              anchor="bottom"
              style={{ zIndex: LANDMARK_PIN_Z_INDEX }}
            >
              <PointOfInterestMarker label={persistedLandmark.current.label} />
            </Marker>
          )}
        </MapboxBaseMap>

        {(offerList?.fetching || currentSearchResult.loadingOffersBanner || isSearchBlocked || isAwaitingTreatmentPinReplace) && (
          <FloatingBanner className={redesign ? 'redesign' : undefined}>
            <TextButton
              kind={floatingStatusKind}
              variant="dark"
              nonInteractable={!offersInteractable || isAwaitingTreatmentPinReplace}
              endIcon={<ColourfulLoadingAnimation size={8} />}
            >
              {loadingOffersText}
            </TextButton>
          </FloatingBanner>
        )}

        {!offerList?.fetching && !currentSearchResult.loadingOffersBanner && !isSearchBlocked && !isAwaitingTreatmentPinReplace && markers.length > 0 && showFloatingTotalCount && (
          <FloatingBanner className={redesign ? 'redesign' : undefined}>
            <TextButton
              kind={floatingStatusKind}
              variant="dark"
              nonInteractable={!offersInteractable}
            >
              {showingCountText}
            </TextButton>
          </FloatingBanner>
        )}

      </MapContainer>
      {zeroResultsBanner && fullScreenMap && !fetchingList && currentSearchResult.noOffersAvailable && (
        <FloatingBanner className={redesign ? 'redesign empty-results' : undefined}>
          <Snackbar
            description={noOffersFoundText}
            kind="neutral"
          />
        </FloatingBanner>
      )}
    </>
  )
}

export default React.memo(SearchResultsMap)
